export class DateBrasilia {
  /**
   * Formata uma data SEMPRE no fuso de Brasília (America/Sao_Paulo), independente
   * do fuso do navegador/ambiente. Use nas abas do relatório administrativo para
   * que o dia exibido não "pule" conforme o tz de quem abre (bug de TMZ — PV-1437).
   * Aceita epoch (ms ou s), ISO string ou Date. Devolve '—' p/ valor inválido/nulo.
   */
  static formatDate(value: string | number | Date | null | undefined, withTime = false): string {
    if (value === null || value === undefined || value === '') return '—';
    const date = typeof value === 'number' ? DateBrasilia.fromTimestamp(value) : new Date(value);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
    });
  }

  /**
   * Normaliza o valor do backend para um instante cujos dígitos UTC são o relógio do
   * agendamento. O agendamento é guardado com o horário escolhido pelo usuário rotulado
   * como UTC ("...10:40:00Z"); strings sem fuso recebem o mesmo tratamento.
   */
  static parseUtc(dateInput: string | Date): Date {
    if (dateInput instanceof Date) {
      return dateInput;
    }
    const hasTimezone = /([zZ])$|[+-]\d{2}:?\d{2}$/.test(dateInput);
    if (hasTimezone) {
      return new Date(dateInput);
    }
    return new Date(`${dateInput.replace(' ', 'T')}Z`);
  }

  /**
   * PV-1485 — exibe o relógio do agendamento EXATAMENTE como armazenado (formata em UTC).
   * NÃO converte para America/Sao_Paulo: o valor já é o horário escolhido pelo usuário
   * (rotulado UTC) e converter aplicava um -3h indevido (10:40 -> 07:40). Comuniare exibe igual.
   * (Diferente de formatDate, que converte instantes reais/epoch dos relatórios admin.)
   */
  static toTzString(dateInput: string | Date): string {
    return this.parseUtc(dateInput).toLocaleString('pt-BR', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  /** "DD/MM/YYYY às HH:mm" ecoando o relógio do agendamento (UTC), sem deslocar fuso (PV-1485). */
  static toBrDateTime(dateInput: string | Date): string {
    const parts = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(this.parseUtc(dateInput));
    const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
    return `${get('day')}/${get('month')}/${get('year')} às ${get('hour')}:${get('minute')}`;
  }

  static startOfDay(dateInput: string | Date): Date {
    const date = new Date(dateInput);

    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  }

  static withTime(dateInput: string | Date, hours: number, minutes: number = 0): Date {
    const date = new Date(dateInput);

    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        3 + hours,
        minutes,
        0,
        0
      )
    );
  }

  static now(): Date {
    const now = new Date();
    return new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 3, 0, 0, 0)
    );
  }

  static fixISO(date1: Date): string {
    const date = new Date(date1);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} às ${hours}:${minutes}`;
  }

  static usaFromBRString(date: string) {
    return date.split('-').reverse().join('/');
  }

  static fromTimestamp(timestamp: number): Date {
    const ts = Math.abs(timestamp) < 1e12 ? timestamp * 1000 : timestamp;
    return new Date(ts);
  }
}
